import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePermissions } from '@/hooks/usePermissions';
import { useFormatting } from '@/hooks/useFormatting';
import { DEMO_COMPLIANCE_ITEMS } from '@/data/demo-data';
import { financialData } from '@/data/financial-systems';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { format, differenceInDays, subDays } from 'date-fns';

export default function GovernanceComplianceCard() {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();

  if (!hasPermission('view_governance_compliance_card')) {
    return null;
  }

  const today = new Date();

  // Derive transaction count from real data — bank transactions logged in the last 7 days
  const sevenDaysAgo = subDays(today, 7);
  const recentTransactionCount = financialData.bankTransactions.filter(
    (txn) => txn.date >= sevenDaysAgo && txn.date <= today
  ).length;
  
  // Categorize compliance items
  const completedItems = DEMO_COMPLIANCE_ITEMS.filter(item => item.status === 'completed');
  const pendingItems = DEMO_COMPLIANCE_ITEMS.filter(item => item.status === 'pending');
  const overdueItems = DEMO_COMPLIANCE_ITEMS.filter(item => item.status === 'overdue');

  // Sort pending items by due date
  const sortedPendingItems = [...pendingItems].sort((a, b) => 
    a.dueDate.getTime() - b.dueDate.getTime()
  );

  const getStatusIcon = (status: string, dueDate: Date) => {
    const daysUntilDue = differenceInDays(dueDate, today);
    
    if (status === 'completed') {
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    } else if (status === 'overdue') {
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    } else if (daysUntilDue <= 10) {
      return <Clock className="h-4 w-4 text-amber-600" />;
    } else {
      return <Clock className="h-4 w-4 text-blue-600" />;
    }
  };

  const getDaysText = (dueDate: Date) => {
    const daysUntilDue = differenceInDays(dueDate, today);
    
    if (daysUntilDue < 0) {
      return `${Math.abs(daysUntilDue)} days overdue`;
    } else if (daysUntilDue === 0) {
      return 'Due today';
    } else if (daysUntilDue === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${daysUntilDue} days`;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Governance & Compliance Status</CardTitle>
          <InfoTooltip
            title="Governance & Compliance Status"
            content="Tracks scheduled payment obligations (VAT, PAYE, Corporation Tax and other recurring commitments), audit readiness indicators, upcoming financial deadlines, and recent audit trail activity. Designed to give the CFO confidence ahead of board review or external audit."
          />
        </div>
      </CardHeader>
      <CardContent>
        {/* 2x2 grid — horizontal divider between rows, vertical divider between columns */}
        <div className="grid grid-cols-2 grid-rows-2 divide-x divide-y divide-border">

          {/* Top-left: Scheduled Payments Status */}
          <div className="space-y-3 p-4">
            <div className="font-semibold text-sm">Scheduled Payments Status</div>
            <div className="space-y-2">
              {completedItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 rounded bg-green-50">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(item.status, item.dueDate)}
                    <span className="text-sm font-medium">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Paid on time ({format(item.dueDate, 'MMM d, yyyy')})
                    </span>
                    {item.amount && (
                      <span className="text-sm font-semibold">{formatCurrency(item.amount)}</span>
                    )}
                  </div>
                </div>
              ))}
              {sortedPendingItems.slice(0, 2).map((item) => {
                const daysUntilDue = differenceInDays(item.dueDate, today);
                const isUrgent = daysUntilDue <= 10;
                return (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between p-2 rounded ${
                      isUrgent ? 'bg-amber-50' : 'bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status, item.dueDate)}
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${isUrgent ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>
                        {getDaysText(item.dueDate)}
                      </span>
                      {item.amount && (
                        <span className="text-sm font-semibold">{formatCurrency(item.amount)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center gap-2 p-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm text-muted-foreground">All obligations compliant</span>
              </div>
            </div>
          </div>

          {/* Top-right: Audit Readiness */}
          <div className="space-y-3 p-4">
            <div className="font-semibold text-sm">Audit Readiness</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-2 rounded bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <div className="text-sm">
                  <div className="font-medium">All transactions categorized</div>
                  <div className="text-muted-foreground">98.5%</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 rounded bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <div className="text-sm">
                  <div className="font-medium">Bank reconciliation complete</div>
                  <div className="text-muted-foreground">100%</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 rounded bg-amber-50">
                <Clock className="h-4 w-4 text-amber-600" />
                <div className="text-sm">
                  <div className="font-medium">Monthly close complete</div>
                  <div className="text-muted-foreground">In progress (8 days remaining)</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 rounded bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <div className="text-sm">
                  <div className="font-medium">Intercompany eliminations</div>
                  <div className="text-muted-foreground">Balanced</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom-left: Upcoming Deadlines */}
          <div className="space-y-3 p-4">
            <div className="font-semibold text-sm">Upcoming Deadlines</div>
            <div className="space-y-2">
              {sortedPendingItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{format(item.dueDate, 'MMM d')}:</span>
                  <span className="font-medium">{item.name}</span>
                  {item.amount && (
                    <span className="font-semibold">{formatCurrency(item.amount)}</span>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Jan 31:</span>
                <span className="font-medium">FY24/25 accounts filing deadline</span>
              </div>
            </div>
          </div>

          {/* Bottom-right: Recent Audit Activity */}
          <div className="space-y-3 p-4">
            <div className="font-semibold text-sm">Recent Audit Activity</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Last 7 days:</span>
                <span className="font-semibold ml-2">{recentTransactionCount} transactions logged</span>
              </div>
              <div>
                <span className="text-muted-foreground">User activity:</span>
                <span className="font-semibold ml-2">Normal</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-muted-foreground">No unusual patterns detected</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-muted-foreground">Last backup: 15m ago</span>
              </div>
            </div>
          </div>

        </div>
      </CardContent>
    </Card>
  );
}
